// src/services/userService.js
// All user profile, address, and cart operations go through the Express backend → MongoDB
// Backend: https://panch-tatva-services.onrender.com (prod) | http://localhost:4000 (dev)

import { logCartEvent } from '../utils/cartLogger';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

/**
 * Helper — authenticated fetch wrapper.
 * Throws on non-2xx. For calls that need the raw status (e.g. 409), use authFetchRaw.
 */
const authFetch = async (url, idToken, options = {}) => {
  const res = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
      ...(options.headers || {}),
    },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || `Request failed: ${res.status}`);
  }
  return res.json();
};

/**
 * Like authFetch but returns { status, data } so the caller can inspect the HTTP
 * status code before deciding what to do (e.g. detect 409 version conflict).
 */
const authFetchRaw = async (url, idToken, options = {}) => {
  const res = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
      ...(options.headers || {}),
    },
  });
  const data = await res.json().catch(() => ({}));
  return { status: res.status, ok: res.ok, data };
};

/**
 * Create user document on first login (idempotent — safe to call every login).
 * POST /api/users/me
 */
export const ensureUserDocument = async (firebaseUser, idToken) => {
  return authFetch(`${API_URL}/api/users/me`, idToken, {
    method: 'POST',
    body: JSON.stringify({
      uid: firebaseUser.uid,
      email: firebaseUser.email,
      displayName: firebaseUser.displayName,
      photoURL: firebaseUser.photoURL,
      firstName: firebaseUser.displayName?.split(' ')[0] || '',
      lastName: firebaseUser.displayName?.split(' ').slice(1).join(' ') || '',
    }),
  });
};

/**
 * Get the logged-in user's profile.
 * GET /api/users/me
 * Returns null if user not found (404).
 */
export const getUserProfile = async (userId, idToken) => {
  try {
    return await authFetch(`${API_URL}/api/users/me`, idToken);
  } catch (err) {
    if (
      err.message?.includes('404') ||
      err.message?.includes('not found') ||
      err.message?.includes('Not Found')
    ) return null;
    throw err;
  }
};

/**
 * Update personal info (firstName, lastName, phone, dateOfBirth).
 * PUT /api/users/me
 */
export const saveUserProfile = async (userId, profileData, idToken) => {
  return authFetch(`${API_URL}/api/users/me`, idToken, {
    method: 'PUT',
    body: JSON.stringify(profileData),
  });
};

/**
 * Add a new address.
 * POST /api/users/me/addresses
 */
export const addAddress = async (userId, address, idToken) => {
  return authFetch(`${API_URL}/api/users/me/addresses`, idToken, {
    method: 'POST',
    body: JSON.stringify(address),
  });
};

/**
 * Delete an address by its id.
 * DELETE /api/users/me/addresses/:addressId
 */
export const removeAddress = async (userId, addressId, idToken) => {
  return authFetch(`${API_URL}/api/users/me/addresses/${addressId}`, idToken, {
    method: 'DELETE',
  });
};

/**
 * Replace the full address array.
 * PUT /api/users/me/addresses
 */
export const updateAddresses = async (userId, addresses, idToken) => {
  return authFetch(`${API_URL}/api/users/me/addresses`, idToken, {
    method: 'PUT',
    body: JSON.stringify({ addresses }),
  });
};

/**
 * Set an address as the default.
 * PATCH /api/users/me/addresses/:addressId/default
 */
export const setDefaultAddress = async (userId, addressId, idToken) => {
  return authFetch(`${API_URL}/api/users/me/addresses/${addressId}/default`, idToken, {
    method: 'PATCH',
  });
};

// ── Cart field-mapping helpers ────────────────────────────────────────────
// Backend uses `productId`; frontend uses `id` throughout.
// These two helpers are the ONLY place the translation happens.
// No other file should perform this translation.

/**
 * Map a frontend cart item { id, ... } → backend shape { productId, ... }
 * Used before every PUT /api/cart call.
 *
 * CRITICAL: If id is missing in dev, throws immediately so the bug is caught
 * before any bad data reaches the backend.
 */
const mapToBackend = (item) => {
  const productId = item.productId || item.id; // tolerates already-mapped items
  if (!productId && process.env.NODE_ENV !== 'production') {
    throw new Error(`[userService] mapToBackend: item has no id or productId — ${JSON.stringify(item)}`);
  }
  return {
    ...item,
    productId,
    id: undefined, // remove frontend field from outbound payload
  };
};

/**
 * Map a backend cart item { productId, ... } → frontend shape { id, ... }
 * Used after every GET /api/cart response.
 *
 * Tolerates items that already have `id` (e.g. legacy data).
 */
const mapFromBackend = (item) => ({
  ...item,
  id: item.productId || item.id, // always set id from productId
  productId: undefined,          // remove backend field from frontend state
});

/**
 * Save (overwrite) the user's cart in MongoDB.
 * PUT /api/cart
 * Body: { items: [ { productId, ... } ], version? }  ← backend shape
 *
 * Returns: { success: true, version: <newVersion> } (when backend supports versioning)
 *          or plain { items } (legacy backend — version field absent)
 *
 * On 409 Conflict (version mismatch):
 *   1. Re-fetch current cart from server
 *   2. Re-merge ORIGINAL local snapshot (not already-merged state) with server items
 *      → prevents double-merge quantity inflation (Issue 1)
 *   3. Retry the PUT once with the fresh version
 *   4. Always update cartVersionRef from server response (Issue 3)
 *   5. If second attempt also fails, throw CartConflictError so the caller can decide
 *
 * @param {string}     idToken
 * @param {Array}      cartItems         — frontend shape (id, not productId); canonical (pre-merged) items to save
 * @param {number|null} version          — current known version, or null if unknown
 * @param {Array|null}  originalLocalItems — ORIGINAL pre-merge local snapshot used for 409 retry merge;
 *                                          if omitted, cartItems is used (safe for non-login flows where
 *                                          cartItems is already the raw local state)
 * @returns {{ version: number|null, mergedItems?: Array }}
 */
export const saveCartToDb = async (idToken, cartItems, version = null, originalLocalItems = null) => {
  // Lazy-import to avoid circular dependency (cartUtils has no imports)
  const { sanitizeCart, mergeCarts } = await import('../utils/cartUtils');

  const buildBody = (items, ver) => {
    const body = { items: items.map(mapToBackend) };
    if (ver !== null && ver !== undefined) body.version = ver;
    return JSON.stringify(body);
  };

  const url = `${API_URL}/api/cart`;
  const { status, ok, data } = await authFetchRaw(url, idToken, {
    method: 'PUT',
    body: buildBody(cartItems, version),
  });

  if (ok) {
    // Detect silent item drops: backend validates and deduplicates before saving.
    // If the response contains fewer items than were sent, some were silently dropped
    // (missing name, missing productId, or duplicate productId).
    const savedCount = data.items?.length ?? cartItems.length;
    const droppedCount = cartItems.length - savedCount;
    if (droppedCount > 0) {
      logCartEvent('cart_items_dropped', {
        sent: cartItems.length,
        saved: savedCount,
        droppedCount,
        context: 'PUT /api/cart — first attempt',
      });
    }
    logCartEvent('cart_saved', { itemCount: cartItems.length, version: data.version ?? null });
    // Issue 3: always return server version so caller can update their ref
    return { version: data.version ?? null };
  }

  if (status === 409) {
    // Version conflict — server has a newer cart.
    logCartEvent('cart_conflict', { clientVersion: version });

    // Step 1: re-fetch server cart to get authoritative items + fresh version
    const serverCart = await loadCartFromDb(idToken); // returns { items, version }
    const freshVersion = serverCart.version;

    // Step 2: Issue 1 fix — merge ORIGINAL local snapshot against server items.
    // Never merge already-merged (cartItems) state — that would double-count quantities.
    // If no original snapshot was passed, fall back to cartItems (non-login flows are safe).
    const localSnapshot = originalLocalItems !== null
      ? sanitizeCart(originalLocalItems)
      : sanitizeCart(cartItems);

    const mergedItems = mergeCarts(localSnapshot, sanitizeCart(serverCart.items));

    // Step 3: retry once with the fresh version from the server
    const { status: s2, ok: ok2, data: d2 } = await authFetchRaw(url, idToken, {
      method: 'PUT',
      body: buildBody(mergedItems, freshVersion),
    });

    if (ok2) {
      // Drop detection on conflict-retry response too
      const savedCount2 = d2.items?.length ?? mergedItems.length;
      const droppedCount2 = mergedItems.length - savedCount2;
      if (droppedCount2 > 0) {
        logCartEvent('cart_items_dropped', {
          sent: mergedItems.length,
          saved: savedCount2,
          droppedCount: droppedCount2,
          context: 'PUT /api/cart — conflict retry',
        });
      }
      logCartEvent('cart_conflict_resolved', { version: d2.version ?? null });
      // Issue 3: always return server version after retry too
      return { version: d2.version ?? null, mergedItems };
    }

    // Second attempt also failed — surface a typed error so CartContext can handle it
    const err = new Error(d2.message || `Cart save failed after conflict retry: ${s2}`);
    err.name = 'CartConflictError';
    err.serverCart = serverCart;
    throw err;
  }

  if (status === 412) {
    // Precondition Failed — server rejected the request due to a precondition mismatch
    // (e.g. ETag or version header). Treat as a no-op: the local state is still valid;
    // the next scheduled sync will retry. Do not throw — do not surface to the user.
    logCartEvent('api_failure', { status: 412, context: 'saveCartToDb', note: 'precondition failed — ignored' });
    return { version: null };
  }

  if (status === 429) {
    // Rate limited — do NOT auto-retry. Surface to CartContext so it can show a
    // "please wait a moment" message. Use a typed error so it can be caught cleanly.
    logCartEvent('api_failure', { status: 429, context: 'saveCartToDb' });
    const err = new Error('Too many requests — please wait a moment before updating your cart.');
    err.name = 'CartRateLimitError';
    throw err;
  }

  if (status === 503) {
    // Service unavailable — backend is down or cart feature is disabled.
    // Fall back to local state silently; CartContext will handle by entering fallback mode.
    logCartEvent('api_failure', { status: 503, context: 'saveCartToDb', note: 'service unavailable — local fallback' });
    const err = new Error('Cart service temporarily unavailable — operating in local mode.');
    err.name = 'CartServiceUnavailableError';
    throw err;
  }

  // Any other non-2xx
  throw new Error(data.message || `Cart save failed: ${status}`);
};

/**
 * Load the user's saved cart from MongoDB.
 * GET /api/cart → normalise to frontend shape { id, ... }.
 *
 * Trust-but-verify: after mapping we run sanitizeCart to strip any legacy DB
 * items that are missing name, id, or other required fields. The backend silently
 * keeps such items if they pre-date the v2.3 validation pipeline; sanitizeCart
 * ensures they never pollute React state.
 *
 * Returns { items: [], version: null } on any error or empty cart.
 */
export const loadCartFromDb = async (idToken) => {
  // Lazy-import to avoid circular dependency (cartUtils has no imports)
  const { sanitizeCart } = await import('../utils/cartUtils');
  try {
    const data = await authFetch(`${API_URL}/api/cart`, idToken);
    const rawItems = (data.items || []).map(mapFromBackend);
    const items = sanitizeCart(rawItems); // trust-but-verify: strip legacy/invalid items
    const version = data.version ?? null;
    logCartEvent('cart_loaded', { itemCount: items.length, version });
    return { items, version };
  } catch {
    return { items: [], version: null };
  }
};

/**
 * Clear the user's cart in MongoDB.
 * DELETE /api/cart — called after order is placed.
 */
export const clearCartInDb = async (idToken) => {
  try {
    await authFetch(`${API_URL}/api/cart`, idToken, { method: 'DELETE' });
  } catch (err) {
    console.warn('Cart DB clear failed:', err.message);
  }
};
